import { useRef, useEffect, useState, useContext } from 'react';
import './signature.css';
import restapi from '../../utils/restapi';
import { PersonContext } from '../../context/PersonContext';
import { useNavigate } from 'react-router-dom';

const PersonSignature = ({ onClose }) => {
  const { person } = useContext(PersonContext);
  const signCanvasRef = useRef(null);
  const [canSave, setCanSave] = useState(false);
  const [signImage, setSignImage] = useState('');
  const navigate = useNavigate();

  // 기존 이미지 불러오기
  useEffect(() => {
    const fetchSignature = async () => {
      if (person && person.person_id) {
        try {
          const res = await restapi.get_person_signature(person.person_id);
          console.log(res);
          if (res && res.signatureImage) setSignImage(res.signatureImage);
        } catch (e) {
          setSignImage('');
        }
      }
    };
    fetchSignature();
    // eslint-disable-next-line
  }, [person]);

  // 캔버스에 이미지 그리기
  useEffect(() => {
    if (signImage && signCanvasRef.current) {
      const img = new window.Image();
      img.onload = () => {
        const canvas = signCanvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        checkCanSave();
      };
      img.src = signImage;
    }
  // eslint-disable-next-line
  }, [signImage]);

  // 캔버스에 그려진 내용이 있는지 확인하는 함수
  const hasDrawing = (canvas) => {
    const ctx = canvas.getContext('2d');
    const pixel = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    // 하나라도 투명하지 않은 픽셀이 있으면 true
    for (let i = 0; i < pixel.length; i += 4) {
      if (pixel[i + 3] !== 0) return true;
    }
    return false;
  };

  // 서명 캔버스가 그려졌는지 체크
  const checkCanSave = () => {
    const signCanvas = signCanvasRef.current;
    if (signCanvas && hasDrawing(signCanvas)) {
      setCanSave(true);
    } else {
      setCanSave(false);
    }
  };

  // 드로잉 훅
  const useCanvasDraw = (canvasRef) => {
    useEffect(() => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      let drawing = false;

      // 실제 렌더링된 크기로 캔버스 크기 설정
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;

      const getPos = (e, canvas) => {
        const rect = canvas.getBoundingClientRect();
        let clientX, clientY;
        if (e.touches && e.touches.length > 0) {
          clientX = e.touches[0].clientX;
          clientY = e.touches[0].clientY;
        } else if (e.nativeEvent) {
          clientX = e.nativeEvent.clientX;
          clientY = e.nativeEvent.clientY;
        } else if (e.clientX !== undefined && e.clientY !== undefined) {
          clientX = e.clientX;
          clientY = e.clientY;
        } else {
          return { x: 0, y: 0 };
        }
        return {
          x: clientX - rect.left,
          y: clientY - rect.top
        };
      };

      const startDraw = (e) => {
        drawing = true;
        ctx.beginPath();
        const pos = getPos(e, canvas);
        ctx.moveTo(pos.x, pos.y);
        if (e.touches) e.preventDefault();
      };
      const draw = (e) => {
        if (!drawing) return;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#222';
        const pos = getPos(e, canvas);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        if (e.touches) e.preventDefault();
      };
      const endDraw = (e) => {
        drawing = false;
        ctx.closePath();
        if (e && e.touches) e.preventDefault();
        checkCanSave(); // 드로잉이 끝날 때마다 저장 가능 여부 체크
      };

      // PC
      canvas.addEventListener('mousedown', startDraw);
      canvas.addEventListener('mousemove', draw);
      canvas.addEventListener('mouseup', endDraw);
      canvas.addEventListener('mouseleave', endDraw);
      // 모바일
      canvas.addEventListener('touchstart', startDraw, { passive: false });
      canvas.addEventListener('touchmove', draw, { passive: false });
      canvas.addEventListener('touchend', endDraw, { passive: false });

      return () => {
        canvas.removeEventListener('mousedown', startDraw);
        canvas.removeEventListener('mousemove', draw);
        canvas.removeEventListener('mouseup', endDraw);
        canvas.removeEventListener('mouseleave', endDraw);
        canvas.removeEventListener('touchstart', startDraw);
        canvas.removeEventListener('touchmove', draw);
        canvas.removeEventListener('touchend', endDraw);
      };
    // eslint-disable-next-line
    }, [canvasRef]);
  };

  useCanvasDraw(signCanvasRef);

  const clearCanvas = async () => {
    const canvas = signCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setTimeout(checkCanSave, 0); // 초기화 후 저장 가능 여부 재확인

    // 서버 이미지도 삭제
    if (person && person.person_id) {
      try {
        await restapi.person_signature_delete(person.person_id);
      } catch (e) {
        alert('서버 이미지 삭제 실패: ' + e.message);
      }
    }
  };

  const handleSaved = async () => {
    const signCanvas = signCanvasRef.current;
    if (signCanvas) {
      const signatureImage = signCanvas.toDataURL('image/png');
      const data = { signatureImage };
      try {
        await restapi.person_signature_update(person.person_id, data);
        alert('서명이 저장되었습니다.');
        window.location.href='/';
      } catch (e) {
        alert('저장 실패 : ' + e.message);
      }
    }
  };

  return (
    <div id="person_signature">
      <div className="head">
        <h1>서명 페이지</h1>
      </div>
      <div className="body">
        <div className="title">
          <h3>서명</h3>
          <button onClick={clearCanvas}>초기화</button>
        </div>
        <div className="content">
          <canvas id="signature-canvas" ref={signCanvasRef}></canvas>
          <p>서명을 작성해 주세요.</p>
        </div>
      </div>
      <div className="foot">
        <button onClick={handleSaved} disabled={!canSave}>저장하기</button>
        {onClose ? (
          <button onClick={onClose}>닫기</button>
        ) : (
          <button onClick={() => navigate('/')}>이전페이지로</button>
        )}
      </div>
    </div>
  );
};

export default PersonSignature;